import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getApiUrl } from '../utils/api';

interface IdleWarning {
  instance_id: string;
  instance_name: string;
  seconds_remaining: number;
  urgent?: boolean;
}

interface IdleNotificationPopupProps {
  warnings: IdleWarning[];
  onDismiss: (instanceId: string) => void;
}

function playAlertTone() {
  try {
    const ctx = new AudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const playTone = (freq: number, startTime: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
      gain.gain.linearRampToValueAtTime(0, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    playTone(520, now, 0.15);
    playTone(660, now + 0.18, 0.15);

    setTimeout(() => ctx.close().catch(() => {}), 1000);
  } catch {
    // Browser autoplay policy or no audio support
  }
}

function formatTime(secs: number) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function IdleNotificationPopup({ warnings, onDismiss }: IdleNotificationPopupProps) {
  const location = useLocation();
  const [countdowns, setCountdowns] = useState<Record<string, number>>({});
  const [loadingAction, setLoadingAction] = useState<Record<string, 'keep-alive' | 'terminate'>>({});
  const playedRef = useRef<Set<string>>(new Set());

  // Hide on compute page — the inline IdleWarningBanner handles it there
  const isComputePage = location.pathname === '/dashboard/compute';

  // Sync incoming warnings into countdowns
  useEffect(() => {
    const initial: Record<string, number> = {};
    for (const w of warnings) {
      if (countdowns[w.instance_id] === undefined) {
        initial[w.instance_id] = w.seconds_remaining;
      }
    }
    if (Object.keys(initial).length > 0) {
      setCountdowns(prev => ({ ...prev, ...initial }));
    }
    // Clean up countdowns for dismissed warnings
    setCountdowns(prev => {
      const activeIds = new Set(warnings.map(w => w.instance_id));
      const next: Record<string, number> = {};
      for (const [id, secs] of Object.entries(prev)) {
        if (activeIds.has(id)) next[id] = secs;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warnings]);

  // 1-second countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns(prev => {
        const next: Record<string, number> = {};
        for (const [id, secs] of Object.entries(prev)) {
          next[id] = Math.max(0, secs - 1);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Play alert tone for new warnings
  useEffect(() => {
    for (const w of warnings) {
      const key = w.urgent ? `urgent-${w.instance_id}` : w.instance_id;
      if (!playedRef.current.has(key)) {
        playedRef.current.add(key);
        playAlertTone();
      }
    }
  }, [warnings]);

  const handleKeepAlive = useCallback(async (instanceId: string) => {
    setLoadingAction(prev => ({ ...prev, [instanceId]: 'keep-alive' }));
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/keep-alive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        console.error('Keep-alive failed:', resp.status, await resp.text().catch(() => ''));
      }
      onDismiss(instanceId);
    } catch (err) {
      console.error('Keep-alive failed:', err);
    } finally {
      setLoadingAction(prev => {
        const next = { ...prev };
        delete next[instanceId];
        return next;
      });
    }
  }, [onDismiss]);

  const handleTerminate = useCallback(async (instanceId: string) => {
    setLoadingAction(prev => ({ ...prev, [instanceId]: 'terminate' }));
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${getApiUrl()}/api/compute/instances/${instanceId}/terminate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        console.error('Terminate failed:', resp.status, await resp.text().catch(() => ''));
      }
      onDismiss(instanceId);
    } catch (err) {
      console.error('Terminate failed:', err);
    } finally {
      setLoadingAction(prev => {
        const next = { ...prev };
        delete next[instanceId];
        return next;
      });
    }
  }, [onDismiss]);

  if (isComputePage) return null;

  const activeWarnings = warnings.filter(w => countdowns[w.instance_id] !== undefined);
  if (activeWarnings.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
      {activeWarnings.map(w => {
        const secs = countdowns[w.instance_id] ?? 0;
        const isUrgent = w.urgent;
        return (
          <div
            key={w.instance_id}
            className={`rounded-lg shadow-2xl border overflow-hidden backdrop-blur-sm ${
              isUrgent
                ? 'bg-red-950/90 border-red-500/60 animate-pulse'
                : 'bg-amber-950/90 border-amber-500/40'
            }`}
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isUrgent ? 'bg-red-500/20' : 'bg-amber-500/20'
                }`}>
                  <svg className={`w-4 h-4 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.345 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold ${isUrgent ? 'text-red-300' : 'text-amber-300'}`}>
                    {isUrgent ? 'Terminating Soon' : 'Idle Shutdown Warning'}
                  </h4>
                  <p className="text-xs text-gray-300 mt-1">
                    <span className="font-mono text-white">{w.instance_name}</span>
                    {isUrgent
                      ? ' will be terminated due to 15 min inactivity.'
                      : ' is idle and will shut down soon.'}
                  </p>
                  <div className={`mt-2 font-mono text-lg font-bold tabular-nums ${
                    isUrgent ? 'text-red-400' : 'text-amber-400'
                  }`}>
                    {formatTime(secs)}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                {(() => {
                  const action = loadingAction[w.instance_id];
                  const isLoading = !!action;
                  return (
                    <>
                      <button
                        onClick={() => handleKeepAlive(w.instance_id)}
                        disabled={isLoading}
                        className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors text-white ${
                          isLoading ? 'opacity-50 cursor-not-allowed' :
                          isUrgent ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-600 hover:bg-amber-500'
                        }`}
                      >
                        {action === 'keep-alive' ? 'Sending...' : 'Keep Working'}
                      </button>
                      <button
                        onClick={() => handleTerminate(w.instance_id)}
                        disabled={isLoading}
                        className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          isLoading ? 'opacity-50 cursor-not-allowed bg-gray-700 text-gray-300' :
                          'bg-gray-700 hover:bg-red-600 text-gray-300 hover:text-white'
                        }`}
                      >
                        {action === 'terminate' ? 'Terminating...' : 'Terminate'}
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
